import { useState } from 'react';
import { LIGHT, MUTE, GOLD, EMBER, BASE, BODY, MONO } from '../lib/constants';
import { Horizon, Btn, Dp, Bd, Mo, GlassCard } from './shared';
import { deleteAccount } from '../lib/api';

/* ═══════════════════════════════════════════════════════════════
   BATCH 7: Delete Account Screen
   Required for Apple App Store approval
   ═══════════════════════════════════════════════════════════════ */

export function DeleteAccountScreen({ onBack, onDelete }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();
      onDelete?.();
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('Failed to delete account. Please try again or contact support.');
      setIsDeleting(false);
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: BASE, padding: '20px 16px' }}>
      <Horizon intensity={0.3} />
      
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 480, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <button
            onClick={onBack}
            style={{
              background: 'transparent', border: 'none', color: GOLD,
              fontFamily: MONO, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6
            }}
            data-testid="back-btn"
          >
            ← BACK
          </button>
          <Dp style={{ fontSize: 28, fontWeight: 800, color: LIGHT, marginBottom: 8 }}>Delete Account</Dp>
          <Bd style={{ fontSize: 13, color: MUTE, lineHeight: 1.6 }}>
            This action is permanent and cannot be undone.
          </Bd>
        </div>

        {/* Warning */}
        <GlassCard style={{
          padding: 20,
          background: 'rgba(232,102,73,0.08)',
          border: '1px solid rgba(232,102,73,0.3)',
          marginBottom: 32
        }}>
          <Mo style={{ fontSize: 10, color: EMBER, fontWeight: 600, letterSpacing: 1.5, marginBottom: 12 }}>
            ⚠️ WHAT WILL BE DELETED
          </Mo>
          <ul style={{ margin: 0, paddingLeft: 20, fontFamily: BODY, fontSize: 13, color: MUTE, lineHeight: 1.8 }}>
            <li>Your user account and profile</li>
            <li>All debt accounts and payment history</li>
            <li>All spending analyses and insights</li>
            <li>All merchant rules and classifications</li>
            <li>All bank connections (via Plaid)</li>
            <li>All preferences, badges, and progress</li>
          </ul>
        </GlassCard>

        {/* Action */}
        {!showConfirm ? (
          <Btn 
            onClick={() => setShowConfirm(true)} 
            style={{
              width: '100%',
              background: 'transparent',
              border: `1.5px solid ${EMBER}`,
              color: EMBER,
              padding: '14px'
            }}
            data-testid="delete-account-btn"
          >
            DELETE MY ACCOUNT
          </Btn>
        ) : (
          <>
            <GlassCard style={{
              padding: 16,
              marginBottom: 16
            }}>
              <Bd style={{ fontSize: 14, color: LIGHT, fontWeight: 600, marginBottom: 8 }}>
                Are you absolutely sure?
              </Bd>
              <Bd style={{ fontSize: 12, color: MUTE, lineHeight: 1.6 }}>
                This will permanently delete all your data. This action cannot be undone.
              </Bd>
            </GlassCard>
            <div style={{ display: 'flex', gap: 12 }}>
              <Btn 
                secondary
                onClick={() => setShowConfirm(false)}
                disabled={isDeleting}
                data-testid="cancel-delete-btn"
              >
                CANCEL
              </Btn>
              <Btn 
                onClick={handleDelete}
                disabled={isDeleting}
                style={{
                  background: EMBER,
                  borderColor: EMBER
                }}
                data-testid="confirm-delete-btn"
              >
                {isDeleting ? 'DELETING...' : 'YES, DELETE PERMANENTLY'}
              </Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
